import React from "react";

import { getProgramInternalName, useGetAllCarbonPrograms, useGetDataSubmissionDates, useNavigate } from "utils";

import { Flex, Link, ListCard, ListCardDataPair, MainPage, Typography } from "components";

import { formatSubmissionDates } from "pages/admin/carbonPrograms/details/programSettingRows";

// The dates recur every year, so only day and month are shown (e.g. "25 November"). UTC, because the API sends midnight UTC
const toDayMonth = (date: Date) => date.toLocaleDateString("en-GB", { day: "numeric", month: "long", timeZone: "UTC" });
const formatIsoDate = (isoDate: string | null | undefined) => (isoDate ? toDayMonth(new Date(isoDate)) : "Not set");
// "04/30" => "30 April" (2000 is a leap year, so 02/29 works)
const formatMonthDay = (monthDay: string) => {
  const [month, day] = monthDay.split("/").map(Number);
  return toDayMonth(new Date(Date.UTC(2000, month - 1, day)));
};

export const Dates = () => {
  const navigate = useNavigate();
  // without a farm, admins get the platform defaults
  const { data: defaultDates, isLoading: isLoadingDefaultDates } = useGetDataSubmissionDates({});
  const { data: carbonPrograms = [], isLoading: isLoadingCarbonPrograms } = useGetAllCarbonPrograms();

  const defaultDatePairs: ListCardDataPair[] = [
    { label: "Start", value: formatIsoDate(defaultDates?.submissionStartDate) },
    { label: "Due", value: formatIsoDate(defaultDates?.submissionDueDate) },
    { label: "End", value: formatIsoDate(defaultDates?.submissionEndDate) },
  ];

  const programDatePairs: ListCardDataPair[] = carbonPrograms.flatMap((program) =>
    program.fieldsManagement.submissionDates
      ? [
          {
            label: getProgramInternalName(program),
            value: (
              <Flex gap={16} align="center">
                {formatSubmissionDates(program.fieldsManagement.submissionDates, formatMonthDay)}
                <Link onClick={() => navigate(`/carbon-programs/details/${program.id}`)}>Details</Link>
              </Flex>
            ),
          },
        ]
      : []
  );

  return (
    <MainPage loading={isLoadingDefaultDates || isLoadingCarbonPrograms} pageName={"admin"}>
      <Flex vertical gap={32}>
        <Typography variant="paragraph-m">
          Submission dates are part of each carbon program&apos;s configuration (<code>fieldsManagement.submissionDates</code>) and are changed in code.
          Programs without their own dates use the platform defaults.
        </Typography>

        <ListCard title="Platform default submission dates" dataPairs={defaultDatePairs} />

        {programDatePairs.length > 0 ? (
          <ListCard title="Programs with their own submission dates" dataPairs={programDatePairs} />
        ) : (
          <Typography variant="paragraph-m">No program sets its own submission dates.</Typography>
        )}
      </Flex>
    </MainPage>
  );
};
